<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { podcastGet, podcastPatch } from "@/common/api/rest/podcast";
import {
  avatarCreate,
  avatarUpdate,
  avatarDelete,
} from "@/common/api/rest/avatar";

import type { TranslationFieldsType } from "@/common/types/translation";
import type { PodcastType } from "@/common/types/podcast";

import { FormPodcast, ListTranslate } from "@/components";
import ListSlug from "@/components/lists/list-slug.vue";
import {
  baseAvatar,
  breadcrumbSimple,
  baseConfirm,
  svgIcon,
} from "@/common/components";

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    label: "common.labels.name",
    value: "",
  },
  {
    type: "textarea",
    field: "description",
    label: "common.labels.description",
    value: "",
  },
]);

const podcastData = ref<PodcastType | null>(null);

const showAvatarDel = ref(false);

const podcastId = computed(() => route.params.podcast as string);

const breadcrumbList = computed(() => [
  {
    key: "podcast",
    label: t("podcast.labels.podcasts"),
    url: `/podcast`,
  },
  {
    key: "edit",
    label: podcastData.value?.title ?? "...",
  },
]);

function back() {
  router.push("/podcast");
}

// podcast
function fetchData() {
  podcastGet(podcastId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        podcastData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(payload: Partial<PodcastType>) {
  podcastPatch(podcastId.value, payload)
    .then((response) => {
      const { success, message } = response;

      if (!success) {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// AVATAR
function addAvatar(avatar: string) {
  if (!store.getters.user) return;

  store.commit("SET_LOADING", true);

  avatarCreate(podcastData.value.podcast_id, avatar)
    .then((response) => {
      const { success, message } = response;

      fetchData();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateAvatar(avatar: string) {
  if (!podcastData.value.avatar) return;

  store.commit("SET_LOADING", true);

  avatarUpdate(podcastData.value.podcast_id, avatar)
    .then((response) => {
      const { success, message } = response;

      fetchData();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeAvatar(avatar: string) {
  if (podcastData.value.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showAvatarDel.value = true;
}

function delAvatarConfirm() {
  if (!podcastData.value?.avatar) return;

  store.commit("SET_LOADING", true);

  avatarDelete(podcastData.value.podcast_id)
    .then((response) => {
      const { success, message } = response;

      fetchData();

      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      showAvatarDel.value = false;
      store.commit("SET_LOADING", false);
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="no-image" size="2rem" />
          {{ t("podcast.labels.edit_podcast") }}
        </div>

        <div class="actions">
          <button class="btn btn-default w-8 h-8" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <div class="flex justify-center">
          <base-avatar
            position="center"
            :is-edit="true"
            :show-image="true"
            :url="podcastData?.avatar?.large"
            size="15rem"
            border-radius="0.25rem"
            @change="changeAvatar($event)"
            @del="delAvatar()"
          />
        </div>

        <form-podcast
          :data="podcastData"
          :is-patch="true"
          @update:data="patchData($event)"
        />

        <div class="separate" />

        <list-slug :owner-id="podcastId" type="PODCAST" />

        <list-translate
          :owner-id="podcastId"
          :language="locale.toLowerCase()"
          :fields="translationFields"
        />
      </div>
    </section>

    <base-confirm
      :show="showAvatarDel"
      :title="t('common.modals.titles.remove_image').toUpperCase()"
      :message="t('common.modals.messages.remove_image')"
      @cancel="showAvatarDel = false"
      @confirm="delAvatarConfirm()"
    />
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .separate {
    border-block-end: 1px solid var(--border);
    margin-top: 1rem;
  }

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
